import React, { createContext, useContext, useMemo } from "react";
import { useQueries } from "react-query";
import { resolveVersion } from "./dataVersion";

interface MapDataContextValue {
  version: string;
}

const MapDataContext = createContext<MapDataContextValue | null>(null);

export interface MapDataResult {
  data: Record<string, any> | null;
  isLoading: boolean;
  error: Error | null;
}

export function getMapDataBasePath(version: string): string {
  if (__CDN_BASE__ === "./") {
    return `./data/${version}/map`;
  }

  return `${__CDN_BASE__.replace(/\/+$/, "")}/data/${version}/map`;
}

export const useDataVersion = (): string => {
  const ctx = useContext(MapDataContext);

  if (!ctx) {
    throw new Error("useDataVersion must be used within MapDataProvider");
  }

  return ctx.version;
};

export const MapDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const version = useMemo(() => resolveVersion(), []);

  return <MapDataContext.Provider value={{ version }}>{children}</MapDataContext.Provider>;
};

export function useMapData(keys: string[]): MapDataResult {
  const version = useDataVersion();
  const basePath = useMemo(() => getMapDataBasePath(version), [version]);
  const queries = useQueries(
    keys.map(key => ({
      queryKey: ["generated-page-data", version, key],
      queryFn: async () => {
        // 以 http(s):// 或 // 开头的 key 视为完整 URL，直接 fetch；
        // 否则按 basePath/${key}.json 拼接。
        const url = /^(https?:)?\/\//.test(key) ? key : `${basePath}/${key}.json`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to load ${key} (${response.status})`);
        }

        return response.json();
      },
      staleTime: Infinity,
      cacheTime: Infinity,
      retry: 0,
    })),
  );

  const isLoading = queries.some(query => query.isLoading);
  const error = queries.find(query => query.error)?.error ?? null;
  const allLoaded = keys.length > 0 && queries.every(query => query.isSuccess);
  const data = useMemo(() => {
    if (!allLoaded) {
      return null;
    }

    const result: Record<string, any> = {};

    keys.forEach((key, index) => {
      result[key] = queries[index].data;
    });

    return result;
  }, [allLoaded, keys, queries]);

  return {
    data,
    isLoading,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
  };
}
